import {
  PackageId,
  SECURITY_DEPOSIT,
  TAX_RATE,
  additionalNightRate,
  formatLongDate,
  priceFor,
  resolveDate,
} from "./pricing";

export interface QuoteLine {
  date: Date;
  isEventNight: boolean;
  description: string;
  amount: number;
}

export interface Quote {
  lines: QuoteLine[];
  venueSubtotal: number;
  tax: number;
  deposit: number;
  total: number;
  dueAtBooking: number;
  dueOneMonthBefore: number;
}

export type QuoteResult =
  | { status: "ok"; quote: Quote }
  | { status: "error"; message: string };

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * Every day the visitor selected, inclusive of both ends — picking Aug 14–15
 * means two days at the venue, so two billed nights. Check-out is the morning
 * after the last selected day.
 */
export const nightsInRange = (rangeStart: Date, rangeEnd: Date) => {
  const nights: Date[] = [];
  for (let d = new Date(rangeStart); d <= rangeEnd; d = addDays(d, 1)) {
    nights.push(new Date(d));
  }
  return nights;
};

/** The morning-after date shown to the visitor and sent in the enquiry. */
export const checkOutDateFor = (rangeEnd: Date) => addDays(rangeEnd, 1);

/** Keeps the event day inside the chosen stay when the range moves. */
export const clampEventDate = (
  eventDate: Date,
  rangeStart: Date,
  rangeEnd: Date,
) => {
  const nights = nightsInRange(rangeStart, rangeEnd);
  if (nights.length === 0) return rangeStart;
  if (nights.some((night) => sameDay(night, eventDate))) return eventDate;
  return nights[nights.length - 1];
};

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Prices a stay: the event night at the package rate for its own season and
 * day type, every other night at the discounted additional-night rate for the
 * season and day type *that night* falls on.
 */
export const buildQuote = (
  pkg: PackageId,
  packageName: string,
  eventDate: Date,
  rangeStart: Date,
  rangeEnd: Date,
  today: Date,
  isBooked: (date: Date) => boolean = () => false,
): QuoteResult => {
  const nights = nightsInRange(rangeStart, rangeEnd);
  if (nights.length === 0) {
    return { status: "error", message: "Choose an end date on or after your start date." };
  }

  // A range can be drawn across a booked day even though that day can't be
  // clicked, so never price a stay we can't actually honour.
  const taken = nights.filter(isBooked);
  if (taken.length > 0) {
    const list = taken.map(formatLongDate);
    return {
      status: "error",
      message:
        taken.length === 1
          ? `${list[0]} is already booked, so we can't hold these dates. Try a range that doesn't cross it.`
          : `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]} are already booked, so we can't hold these dates. Try a range that doesn't cross them.`,
    };
  }

  const lines: QuoteLine[] = [];
  for (const night of nights) {
    const resolved = resolveDate(night, today);
    if (resolved.status === "closed") {
      return {
        status: "error",
        message: `We're closed on ${formatLongDate(night)}. The venue is closed December 1 through February 28.`,
      };
    }
    if (resolved.status === "past") {
      return { status: "error", message: "Choose dates in the future." };
    }
    if (resolved.status === "unpriced") {
      return {
        status: "error",
        message: `We haven't published pricing for ${night.getFullYear()} yet — get in touch and we'll quote it for you.`,
      };
    }

    const { year, tier, dayType } = resolved.context;
    const isEventNight = sameDay(night, eventDate);
    const dayLabel = dayType === "weekend" ? "Fri–Sun" : "Mon–Thu";
    lines.push({
      date: night,
      isEventNight,
      description: isEventNight
        ? `${packageName} package — ${dayLabel} rate`
        : `Additional night — 30% off the Hummingbird ${dayLabel} rate`,
      amount: isEventNight
        ? priceFor(pkg, year, tier, dayType)
        : additionalNightRate(year, tier, dayType),
    });
  }

  const venueSubtotal = lines.reduce((sum, line) => sum + line.amount, 0);
  const tax = round2(venueSubtotal * TAX_RATE);
  const deposit = SECURITY_DEPOSIT;
  const total = round2(venueSubtotal + tax + deposit);
  // Split so the two payments always add back to the total exactly.
  const dueAtBooking = round2(total / 2);
  const dueOneMonthBefore = round2(total - dueAtBooking);

  return {
    status: "ok",
    quote: {
      lines,
      venueSubtotal,
      tax,
      deposit,
      total,
      dueAtBooking,
      dueOneMonthBefore,
    },
  };
};

export const formatMoney = (amount: number) =>
  amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const BOOKING_EMAIL = "booking@prettybirdwedding.com";

/**
 * Matches the enquiry format used on the booking page, with the package,
 * dates and quoted total filled in.
 */
export const buildBookingMailto = (
  packageName: string,
  eventDate: Date,
  rangeStart: Date,
  rangeEnd: Date,
  quote: Quote,
) => {
  const subject = `Booking request — ${packageName}, ${formatLongDate(eventDate)}`;
  const body = [
    "Name: ",
    "Contact Email: ",
    "Phone number: ",
    `Dates desired: check in ${formatLongDate(rangeStart)}, check out ${formatLongDate(checkOutDateFor(rangeEnd))}`,
    `Event date: ${formatLongDate(eventDate)}`,
    `Package: ${packageName}`,
    "Number of Guests: ",
    `Quoted total: ${formatMoney(quote.total)} (includes ${formatMoney(quote.tax)} tax and a ${formatMoney(quote.deposit)} refundable security deposit)`,
    "Any Additional Items or Questions: ",
  ].join("\r\n");

  return `mailto:${BOOKING_EMAIL}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;
};

/** Same content as the mailto, for visitors without a mail app configured. */
export const buildBookingEmailText = (
  packageName: string,
  eventDate: Date,
  rangeStart: Date,
  rangeEnd: Date,
  quote: Quote,
) =>
  [
    `To: ${BOOKING_EMAIL}`,
    `Subject: Booking request — ${packageName}, ${formatLongDate(eventDate)}`,
    "",
    "Name:",
    "Contact Email:",
    "Phone number:",
    `Dates desired: check in ${formatLongDate(rangeStart)}, check out ${formatLongDate(checkOutDateFor(rangeEnd))}`,
    `Event date: ${formatLongDate(eventDate)}`,
    `Package: ${packageName}`,
    "Number of Guests:",
    `Quoted total: ${formatMoney(quote.total)}`,
    "Any Additional Items or Questions:",
  ].join("\n");
